import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  CircularProgress,
  Alert,
  Link,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Contacts as ContactsIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { getCRMContacts, getCRMReps, getDToolsDirectory, getZohoContacts, getZohoContactTypes, getUnifiedContacts, getZohoReps, getCRMCompanies, assignCompanyRep, removeCompanyRep, getUsers, getCompanyTypes, setCompanyType, createGlobalContact, updateGlobalContact, deleteGlobalContact, getMailingList, getCompaniesForMailing, bulkImportContacts } from '../services/api';
import { Pagination, Tabs, Tab, Checkbox, FormControlLabel, FormGroup, Snackbar, Switch } from '@mui/material';
import { DISCIPLINES, ACS_OFFICES } from '../constants/crm';
import PageSearchBar from '../components/PageSearchBar';

// ─── Role chip config ─────────────────────────────────────────────────────────

const ROLE_COLORS = {
  architect:            { bg: '#1565C0', label: 'Architect' },
  builder:              { bg: '#2E7D32', label: 'Builder' },
  general_contractor:   { bg: '#388E3C', label: 'General Contractor' },
  interior_designer:    { bg: '#6A1B9A', label: 'Designer' },
  lighting_consultant:  { bg: '#F57C00', label: 'Lighting Consultant' },
  client_rep:           { bg: '#00695C', label: 'Client Rep' },
  electrician:          { bg: '#E65100', label: 'Electrician' },
  developer:            { bg: '#283593', label: 'Developer' },
  real_estate:          { bg: '#00838F', label: 'Real Estate' },
  engineer:             { bg: '#4E342E', label: 'Engineer' },
  commercial:           { bg: '#37474F', label: 'Commercial' },
  construction:         { bg: '#558B2F', label: 'Construction' },
  hvac:                 { bg: '#BF360C', label: 'HVAC' },
  other:                { bg: '#757575', label: 'Other' },
  client:               { bg: '#546E7A', label: 'Client' },
  property_manager:     { bg: '#5D4037', label: 'Property Mgr' },
  admin:                { bg: '#78909C', label: 'Admin' },
  executive:            { bg: '#1A237E', label: 'Executive' },
  project_manager:      { bg: '#2E7D32', label: 'Project Mgr' },
};

function RoleChip({ role }) {
  const cfg = ROLE_COLORS[role] || { bg: '#78909C', label: role };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
      }}
    />
  );
}

// ─── Health status dot ────────────────────────────────────────────────────────

const STATUS_COLORS = {
  green:  '#2E7D32',
  yellow: '#F9A825',
  red:    '#C62828',
  gray:   '#9E9E9E',
};

function StatusDot({ status }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.gray;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        mr: 0.75,
        flexShrink: 0,
      }}
    />
  );
}

// ─── Currency formatter ───────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val == null || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

// ─── Expanded project sub-table ───────────────────────────────────────────────

function ProjectSubTable({ projects }) {
  const navigate = useNavigate();

  if (!projects || projects.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        No active projects found.
      </Typography>
    );
  }

  return (
    <Table size="small" sx={{ '& td, & th': { fontSize: '0.78rem' } }}>
      <TableHead>
        <TableRow sx={{ bgcolor: '#f0f4f8' }}>
          <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Job #</TableCell>
          <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Project Name</TableCell>
          <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Contract Value</TableCell>
          <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Status</TableCell>
          <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Phase</TableCell>
          <TableCell sx={{ fontWeight: 700, py: 0.75 }}>PM</TableCell>
          <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Sales Rep</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {projects.map((proj, i) => (
          <TableRow
            key={proj.id || i}
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => proj.id && navigate(`/projects/${proj.id}`)}
          >
            <TableCell sx={{ py: 0.6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {proj.job_number || '—'}
                </Typography>
                {proj.id && (
                  <OpenInNewIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                )}
              </Box>
            </TableCell>
            <TableCell sx={{ py: 0.6, maxWidth: 240 }}>
              <Typography variant="body2" noWrap>
                {proj.name || '—'}
              </Typography>
            </TableCell>
            <TableCell sx={{ py: 0.6 }}>
              {fmtCurrency(proj.contract_value)}
            </TableCell>
            <TableCell sx={{ py: 0.6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusDot status={proj.status} />
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {proj.status || '—'}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ py: 0.6 }}>
              {proj.phase || '—'}
            </TableCell>
            <TableCell sx={{ py: 0.6 }}>
              {proj.pm || '—'}
            </TableCell>
            <TableCell sx={{ py: 0.6 }}>
              {proj.sales_rep || '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── Contact row ──────────────────────────────────────────────────────────────

function ContactRow({ contact, index }) {
  const [open, setOpen] = useState(false);

  const visibleProjects = (contact.projects || []).slice(0, 3);
  const extraCount = (contact.projects || []).length - 3;

  const isEven = index % 2 === 0;

  return (
    <>
      <TableRow
        hover
        sx={{
          bgcolor: isEven ? '#fff' : '#fafafa',
          cursor: 'pointer',
          '&:hover': { bgcolor: '#f0f4f8' },
        }}
        onClick={() => setOpen(!open)}
      >
        {/* Company / Contact */}
        <TableCell sx={{ py: 1, minWidth: 180 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {contact.company_name || '—'}
          </Typography>
          {contact.contact_name && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {contact.title ? `${contact.contact_name} · ${contact.title}` : contact.contact_name}
            </Typography>
          )}
        </TableCell>

        {/* Type chip */}
        <TableCell sx={{ py: 1 }}>
          <RoleChip role={contact.role} />
        </TableCell>

        {/* Contact info */}
        <TableCell sx={{ py: 1, minWidth: 160 }}>
          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
              <EmailIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
              <Link
                href={`mailto:${contact.email}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ fontSize: '0.75rem', color: '#1A5490', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
              >
                {contact.email}
              </Link>
            </Box>
          )}
          {contact.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
              <Link
                href={`tel:${contact.phone}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ fontSize: '0.75rem', color: '#1A5490' }}
              >
                {contact.phone}
              </Link>
            </Box>
          )}
          {!contact.email && !contact.phone && (
            <Typography variant="caption" color="text.disabled">No contact info</Typography>
          )}
        </TableCell>

        {/* Primary sales rep */}
        <TableCell sx={{ py: 1 }}>
          <Typography variant="body2" noWrap>
            {contact.primary_rep || '—'}
          </Typography>
        </TableCell>

        {/* Projects count + mini list */}
        <TableCell sx={{ py: 1, minWidth: 160 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={contact.project_count}
              size="small"
              sx={{ bgcolor: '#1A5490', color: '#fff', fontWeight: 700, height: 20, minWidth: 28, fontSize: '0.72rem' }}
            />
            {visibleProjects.map((p, i) => (
              <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace', color: '#555' }}>
                {p.job_number || `#${p.id}`}
              </Typography>
            ))}
            {extraCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                +{extraCount} more
              </Typography>
            )}
          </Box>
        </TableCell>

        {/* Total value */}
        <TableCell sx={{ py: 1, minWidth: 110 }}>
          <Typography variant="body2" fontWeight={600}>
            {fmtCurrency(contact.total_contract_value)}
          </Typography>
        </TableCell>

        {/* Expand toggle */}
        <TableCell sx={{ py: 1, width: 40 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Expanded project sub-table */}
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: open ? '2px solid #e0e0e0' : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, pl: 3, pr: 1, bgcolor: '#f7f9fc' }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                Active Projects ({contact.project_count})
              </Typography>
              <ProjectSubTable projects={contact.projects} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ─── Company-Centric CRM View ───────────────────────────────────────────────

function CompanyDirectory() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('all');
  const [types, setTypes] = useState([]);
  const [reps, setReps] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [repEditCompany, setRepEditCompany] = useState(null);
  const [repAddUser, setRepAddUser] = useState(null);
  const [repSaving, setRepSaving] = useState(false);
  const [typeEditCompany, setTypeEditCompany] = useState(null);
  const [typeCatalog, setTypeCatalog] = useState([]);
  const [typeSaving, setTypeSaving] = useState(false);
  const [sortField, setSortField] = useState('people_count');
  const [sortDir, setSortDir] = useState('desc');
  const perPage = 50;

  // Manual contact dialog state
  const [contactDialog, setContactDialog] = useState(null); // null | { mode: 'add'|'edit', data: {...} }
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState(null);

  useEffect(() => {
    getZohoContactTypes().then(r => setTypes(r.data.types || [])).catch(() => {});
    getZohoReps().then(r => setReps(r.data.reps || [])).catch(() => {});
    getCompanyTypes().then(r => setTypeCatalog(r.data.types || [])).catch(() => {});
    getUsers().then(r => {
      const users = (r.data || []).filter(u => u.active !== false).sort((a, b) => a.name.localeCompare(b.name));
      setAllUsers(users);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, per_page: perPage };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (repFilter !== 'all') params.rep = repFilter;
      if (search) params.search = search;
      params.sort = sortField;
      params.dir = sortDir;
      const res = await getCRMCompanies(params);
      setCompanies(res.data.companies || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, repFilter, page, sortField, sortDir]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, typeFilter, repFilter]);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  const totalPages = Math.ceil(total / perPage);

  const handleColumnSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'company_name' || field === 'company_type' || field === 'acs_rep' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const handleToggleType = async (company, typeId) => {
    setTypeSaving(true);
    try {
      const currentIds = company.type_ids || [];
      const newIds = currentIds.includes(typeId)
        ? currentIds.filter(id => id !== typeId)
        : [...currentIds, typeId];
      await setCompanyType({
        company_key: company.company_key,
        company_name: company.company_name,
        type_ids: newIds,
      });
      load();
    } catch (err) {
      console.error('Type assign error:', err);
    } finally {
      setTypeSaving(false);
    }
  };

  const handleAssignRep = async (company, userId) => {
    if (!userId) return;
    setRepSaving(true);
    try {
      await assignCompanyRep({
        company_key: company.company_key,
        company_name: company.company_name,
        user_id: userId,
        role: 'primary',
      });
      setRepEditCompany(null);
      setRepAddUser(null);
      load(); // refresh
    } catch (err) {
      console.error('Assign rep error:', err);
    } finally {
      setRepSaving(false);
    }
  };

  const handleRemoveRep = async (assignmentId) => {
    try {
      await removeCompanyRep(assignmentId);
      load();
    } catch (err) {
      console.error('Remove rep error:', err);
    }
  };

  // Manual contact handlers
  const openAddContact = (prefillCompany) => {
    setContactError(null);
    setContactDialog({
      mode: 'add',
      data: {
        name: '',
        company: prefillCompany || '',
        role_title: '',
        category: '',
        email: '',
        phone_office: '',
        phone_mobile: '',
        notes: '',
      },
    });
  };
  const openEditContact = (person, companyName) => {
    setContactError(null);
    setContactDialog({
      mode: 'edit',
      data: {
        id: person.id,
        name: person.name || '',
        company: companyName || '',
        role_title: person.title || '',
        category: person.type || '',
        email: person.email || '',
        phone_office: person.phone || '',
        phone_mobile: '',
        notes: person.notes || '',
      },
    });
  };
  const closeContactDialog = () => { setContactDialog(null); setContactError(null); };
  const saveContact = async () => {
    if (!contactDialog?.data?.name?.trim()) {
      setContactError('Name is required');
      return;
    }
    setContactSaving(true);
    setContactError(null);
    try {
      const { id, ...payload } = contactDialog.data;
      if (contactDialog.mode === 'add') {
        await createGlobalContact(payload);
      } else {
        await updateGlobalContact(id, payload);
      }
      closeContactDialog();
      load();
    } catch (err) {
      setContactError(err.response?.data?.error || 'Failed to save');
    } finally {
      setContactSaving(false);
    }
  };
  const deleteContact = async (id) => {
    if (!window.confirm('Delete this manual contact? This cannot be undone.')) return;
    try {
      await deleteGlobalContact(id);
      load();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  // List of company names to suggest in the Autocomplete
  const companyNameOptions = React.useMemo(() => {
    const names = new Set();
    companies.forEach(c => { if (c.company_name && c.company_key !== '__manual_unassigned__') names.add(c.company_name); });
    return [...names].sort();
  }, [companies]);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <PageSearchBar
            placeholder="Search company, person, email…"
            value={searchInput}
            onChange={(v) => setSearchInput(v)}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
              <MenuItem value="all">All Types</MenuItem>
              {types.map(t => <MenuItem key={t.contact_type} value={t.contact_type}>{t.contact_type} ({t.cnt})</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>ACS Rep</InputLabel>
            <Select value={repFilter} label="ACS Rep" onChange={e => setRepFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
              <MenuItem value="all">All Reps</MenuItem>
              {reps.map(r => <MenuItem key={r.owner_name} value={r.owner_name}>{r.owner_name} ({r.cnt})</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => openAddContact()}
            sx={{ bgcolor: '#1A5490', '&:hover': { bgcolor: '#0d3560' }, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Add Contact
          </Button>
          {!loading && <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{total} companies</Typography>}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 310px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem', width: 40 }} />
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel active={sortField === 'company_name'} direction={sortField === 'company_name' ? sortDir : 'asc'} onClick={() => handleColumnSort('company_name')}>Company</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel active={sortField === 'company_type'} direction={sortField === 'company_type' ? sortDir : 'asc'} onClick={() => handleColumnSort('company_type')}>Type</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel active={sortField === 'people_count'} direction={sortField === 'people_count' ? sortDir : 'desc'} onClick={() => handleColumnSort('people_count')}>People</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Company Contact</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel active={sortField === 'acs_rep'} direction={sortField === 'acs_rep' ? sortDir : 'asc'} onClick={() => handleColumnSort('acs_rep')}>ACS Rep</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel active={sortField === 'project_count'} direction={sortField === 'project_count' ? sortDir : 'desc'} onClick={() => handleColumnSort('project_count')}>Projects</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel active={sortField === 'total_contract_value'} direction={sortField === 'total_contract_value' ? sortDir : 'desc'} onClick={() => handleColumnSort('total_contract_value')}>Contract Value</TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={28} /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading companies…</Typography></TableCell></TableRow>
              ) : companies.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><Typography variant="body2" color="text.secondary">No companies found.</Typography></TableCell></TableRow>
              ) : (
                companies.map((co, idx) => {
                  const isOpen = !!expanded[co.company_key];
                  const typeCfg = ROLE_COLORS[co.company_type?.toLowerCase()?.replace(/ /g, '_')] || ROLE_COLORS[co.company_type?.toLowerCase()] || null;
                  return (
                    <React.Fragment key={co.company_key}>
                      <TableRow
                        hover
                        sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa', cursor: co.people_count > 0 ? 'pointer' : 'default' }}
                        onClick={() => co.people_count > 0 && toggleExpand(co.company_key)}
                      >
                        <TableCell sx={{ py: 0.75 }}>
                          {co.people_count > 0 && (
                            <IconButton size="small">
                              {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Typography variant="body2" fontWeight={700}>{co.company_name}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, minWidth: 120 }} onClick={e => e.stopPropagation()}>
                          {typeEditCompany?.company_key === co.company_key ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                                {typeCatalog.map(t => {
                                  const isSelected = (co.type_ids || []).includes(t.id);
                                  return (
                                    <Chip
                                      key={t.id}
                                      label={t.name}
                                      size="small"
                                      onClick={() => handleToggleType(co, t.id)}
                                      disabled={typeSaving}
                                      sx={{
                                        bgcolor: isSelected ? (t.color || '#757575') : 'transparent',
                                        color: isSelected ? '#fff' : 'text.secondary',
                                        border: isSelected ? 'none' : '1px solid #ccc',
                                        fontWeight: isSelected ? 700 : 400,
                                        fontSize: '0.65rem', height: 22, cursor: 'pointer',
                                        '&:hover': { opacity: 0.85 },
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                              <Button size="small" onClick={() => setTypeEditCompany(null)} sx={{ fontSize: '0.7rem', p: 0, minWidth: 0, alignSelf: 'flex-start' }}>Done</Button>
                            </Box>
                          ) : (
                            <Box
                              sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0f4f8' }, borderRadius: 0.5, px: 0.5, py: 0.25, mx: -0.5, display: 'flex', flexWrap: 'wrap', gap: 0.3 }}
                              onClick={() => setTypeEditCompany(co)}
                            >
                              {co.company_type ? (
                                co.company_type.split(', ').map((t, ti) => {
                                  const cfg = ROLE_COLORS[t.toLowerCase()?.replace(/ /g, '_')] || ROLE_COLORS[t.toLowerCase()] || null;
                                  return cfg
                                    ? <Chip key={ti} label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                                    : <Chip key={ti} label={t} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />;
                                })
                              ) : (
                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Click to assign</Typography>
                              )}
                              {co.type_source === 'zoho' && co.company_type && (
                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', ml: 0.3 }}>(Zoho)</Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Chip
                            label={co.people_count}
                            size="small"
                            sx={{ bgcolor: co.people_count > 0 ? '#1A5490' : '#e0e0e0', color: co.people_count > 0 ? '#fff' : '#999', fontWeight: 700, fontSize: '0.72rem', height: 20, minWidth: 28 }}
                          />
                          {co.people_count > 0 && !isOpen && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {co.people.slice(0, 2).map(p => p.name).join(', ')}
                              {co.people_count > 2 && ` +${co.people_count - 2}`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {co.company_email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                              <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Link href={`mailto:${co.company_email}`} onClick={e => e.stopPropagation()} sx={{ fontSize: '0.73rem', color: '#1A5490' }}>{co.company_email}</Link>
                            </Box>
                          )}
                          {co.company_phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Typography variant="caption">{co.company_phone}</Typography>
                            </Box>
                          )}
                          {!co.company_email && !co.company_phone && <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, minWidth: 140 }} onClick={e => e.stopPropagation()}>
                          {repEditCompany?.company_key === co.company_key ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {(co.acs_reps || []).filter(r => r.source === 'assigned').map(r => (
                                <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" fontWeight={600}>{r.name}</Typography>
                                  <IconButton size="small" onClick={() => handleRemoveRep(r.id)} sx={{ p: 0.25 }}>
                                    <CloseIcon sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </Box>
                              ))}
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value=""
                                  displayEmpty
                                  onChange={e => handleAssignRep(co, e.target.value)}
                                  sx={{ fontSize: '0.75rem', height: 28 }}
                                  disabled={repSaving}
                                >
                                  <MenuItem value="" disabled>{repSaving ? 'Saving…' : 'Add rep…'}</MenuItem>
                                  {allUsers.filter(u => !(co.acs_reps || []).some(r => r.user_id === u.id)).map(u => (
                                    <MenuItem key={u.id} value={u.id} sx={{ fontSize: '0.78rem' }}>{u.name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Button size="small" onClick={() => setRepEditCompany(null)} sx={{ fontSize: '0.7rem', p: 0, minWidth: 0 }}>Done</Button>
                            </Box>
                          ) : (
                            <Box
                              sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0f4f8' }, borderRadius: 0.5, px: 0.5, py: 0.25, mx: -0.5 }}
                              onClick={() => setRepEditCompany(co)}
                            >
                              {(co.acs_reps || []).length > 0 ? (
                                co.acs_reps.map((r, ri) => (
                                  <Typography key={ri} variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                                    {r.name}
                                    {r.source === 'zoho' && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>(Zoho)</Typography>}
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Click to assign</Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {co.project_count > 0 ? (
                            <Chip label={co.project_count} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.72rem', height: 20, minWidth: 28 }} />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {co.total_contract_value > 0 ? (
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem' }}>
                              ${(co.total_contract_value / 1000).toFixed(0)}K
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded: people under this company */}
                      {isOpen && co.people.map((person, pi) => {
                        const pType = (person.type || '').replace(/[{}"]/g, '').trim();
                        const pCfg = ROLE_COLORS[pType.toLowerCase()?.replace(/ /g, '_')] || null;
                        const modCfg = MODULE_COLORS[person.module] || { bg: '#757575', label: person.module };
                        return (
                          <TableRow key={`${co.company_key}-p${pi}`} sx={{ bgcolor: '#f7f9fc' }}>
                            <TableCell />
                            <TableCell sx={{ py: 0.5, pl: 4 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={600}>{person.name}</Typography>
                                {person.is_manual && (
                                  <>
                                    <Tooltip title="Edit manual contact">
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditContact(person, co.company_key === '__manual_unassigned__' ? '' : co.company_name); }} sx={{ p: 0.25 }}>
                                        <EditIcon sx={{ fontSize: 13, color: '#1A5490' }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete manual contact">
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteContact(person.id); }} sx={{ p: 0.25 }}>
                                        <DeleteIcon sx={{ fontSize: 13, color: '#c62828' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                              {person.title && <Typography variant="caption" color="text.secondary" display="block">{person.title}</Typography>}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              {pCfg ? (
                                <Chip label={pCfg.label} size="small" sx={{ bgcolor: pCfg.bg, color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
                              ) : pType ? (
                                <Chip label={pType} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Chip label={modCfg.label} size="small" sx={{ bgcolor: modCfg.bg, color: '#fff', fontSize: '0.6rem', height: 16, opacity: 0.8 }} />
                            </TableCell>
                            <TableCell colSpan={2} sx={{ py: 0.5 }}>
                              {person.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                  <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                  <Link href={`mailto:${person.email}`} sx={{ fontSize: '0.73rem', color: '#1A5490' }}>{person.email}</Link>
                                </Box>
                              )}
                              {person.phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                  <Typography variant="caption">{person.phone}</Typography>
                                </Box>
                              )}
                              {!person.email && !person.phone && <Typography variant="caption" color="text.disabled">No contact info</Typography>}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              {co.company_key !== '__manual_unassigned__' && (
                                <Tooltip title="Add contact to this company">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openAddContact(co.company_name); }} sx={{ p: 0.25 }}>
                                    <AddIcon sx={{ fontSize: 14, color: '#1A5490' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: '#f5f7fa', borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary">{total} companies</Typography>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" />
          </Box>
        )}
      </Paper>

      {/* Manual contact dialog */}
      <Dialog open={!!contactDialog} onClose={closeContactDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700, bgcolor: '#1A5490', color: '#fff', py: 1.5 }}>
          {contactDialog?.mode === 'edit' ? 'Edit Manual Contact' : 'Add Manual Contact'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          {contactError && <Alert severity="error" sx={{ mb: 2 }}>{contactError}</Alert>}
          {contactDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <TextField
                size="small" autoFocus required
                label="Full Name"
                value={contactDialog.data.name}
                onChange={e => setContactDialog(d => ({ ...d, data: { ...d.data, name: e.target.value } }))}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={companyNameOptions}
                value={contactDialog.data.company || ''}
                onChange={(_, v) => setContactDialog(d => ({ ...d, data: { ...d.data, company: v || '' } }))}
                onInputChange={(_, v) => setContactDialog(d => ({ ...d, data: { ...d.data, company: v || '' } }))}
                renderInput={(params) => (
                  <TextField {...params} label="Company" placeholder="Existing company or new name" helperText="Pick from existing CRM companies, or type a new one. Leave blank to file under Unassigned." />
                )}
              />
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  size="small" fullWidth
                  label="Title / Role"
                  value={contactDialog.data.role_title}
                  onChange={e => setContactDialog(d => ({ ...d, data: { ...d.data, role_title: e.target.value } }))}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={contactDialog.data.category || ''}
                    onChange={e => setContactDialog(d => ({ ...d, data: { ...d.data, category: e.target.value } }))}
                  >
                    <MenuItem value="">—</MenuItem>
                    {typeCatalog.map(t => (
                      <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                size="small"
                label="Email"
                type="email"
                value={contactDialog.data.email}
                onChange={e => setContactDialog(d => ({ ...d, data: { ...d.data, email: e.target.value } }))}
              />
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  size="small" fullWidth
                  label="Office Phone"
                  value={contactDialog.data.phone_office}
                  onChange={e => setContactDialog(d => ({ ...d, data: { ...d.data, phone_office: e.target.value } }))}
                />
                <TextField
                  size="small" fullWidth
                  label="Mobile"
                  value={contactDialog.data.phone_mobile}
                  onChange={e => setContactDialog(d => ({ ...d, data: { ...d.data, phone_mobile: e.target.value } }))}
                />
              </Box>
              <TextField
                size="small" multiline minRows={2}
                label="Notes"
                value={contactDialog.data.notes}
                onChange={e => setContactDialog(d => ({ ...d, data: { ...d.data, notes: e.target.value } }))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeContactDialog} disabled={contactSaving}>Cancel</Button>
          <Button onClick={saveContact} variant="contained" disabled={contactSaving} sx={{ bgcolor: '#1A5490', '&:hover': { bgcolor: '#0d3560' } }}>
            {contactSaving ? 'Saving…' : (contactDialog?.mode === 'edit' ? 'Save Changes' : 'Add Contact')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── D-Tools Directory Component ─────────────────────────────────────────────

const DTOOLS_ROLES = [
  { value: '', label: 'All Roles' },
  { value: 'client', label: 'Client / Owner' },
  { value: 'client_rep', label: 'Client Rep' },
  { value: 'architect', label: 'Architect' },
  { value: 'interior_designer', label: 'Designer' },
  { value: 'general_contractor', label: 'GC / Builder' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'property_manager', label: 'Property / House Mgr' },
  { value: 'executive', label: 'Executive' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'admin', label: 'Admin / Billing' },
  { value: 'other', label: 'Other' },
];

function DToolsDirectory() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [companyType, setCompanyType] = useState('all');
  const [stats, setStats] = useState({});
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { company_type: companyType };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await getDToolsDirectory(params);
      setCompanies(res.data.companies || []);
      setStats({
        total_companies: res.data.total_companies,
        total_persons: res.data.total_persons,
        with_projects: res.data.with_projects,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load D-Tools directory');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, companyType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <Box>
      {/* Filter bar */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <PageSearchBar
            placeholder="Search company, person, email…"
            value={searchInput}
            onChange={(v) => setSearchInput(v)}
          />

          <ToggleButtonGroup
            value={companyType}
            exclusive
            onChange={(_, v) => { if (v) setCompanyType(v); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.8rem', textTransform: 'none' } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="customer">Clients (AR)</ToggleButton>
            <ToggleButton value="vendor">Vendors</ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Contact Role</InputLabel>
            <Select
              value={roleFilter}
              label="Contact Role"
              onChange={e => setRoleFilter(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              {DTOOLS_ROLES.map(r => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {!loading && (
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                {stats.total_companies} companies · {stats.total_persons} contacts · {stats.with_projects} linked to projects
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Directory table */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem', width: 40 }} />
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Contacts</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Company Info</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Projects</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading D-Tools directory…</Typography>
                  </TableCell>
                </TableRow>
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No contacts found matching filters.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                companies.slice(0, 200).map((company, idx) => {
                  const isOpen = !!expanded[company.company_id];
                  return (
                    <React.Fragment key={company.company_id}>
                      <TableRow
                        hover
                        sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa', cursor: company.persons.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => company.persons.length > 0 && toggleExpand(company.company_id)}
                      >
                        <TableCell sx={{ py: 0.75, width: 40 }}>
                          {company.persons.length > 0 && (
                            <IconButton size="small">
                              {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>{company.company_name}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Chip
                            label={company.company_type === 'AR Customer' ? 'Client' : company.company_type || '—'}
                            size="small"
                            sx={{
                              bgcolor: company.company_type === 'AR Customer' ? '#546E7A' : company.company_type === 'Vendor' ? '#E65100' : '#9E9E9E',
                              color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 22,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Chip
                            label={company.persons.length || '—'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.72rem', height: 20, minWidth: 28 }}
                          />
                          {company.persons.length > 0 && !isOpen && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {company.persons.slice(0, 2).map(p => p.name).join(', ')}
                              {company.persons.length > 2 && ` +${company.persons.length - 2}`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {company.company_email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Link href={`mailto:${company.company_email}`} onClick={e => e.stopPropagation()} sx={{ fontSize: '0.75rem' }}>{company.company_email}</Link>
                            </Box>
                          )}
                          {company.company_phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Typography variant="caption">{company.company_phone}</Typography>
                            </Box>
                          )}
                          {!company.company_email && !company.company_phone && (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {company.has_projects ? (
                            <Chip label={company.ar_code} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded persons */}
                      {isOpen && company.persons.map((person, pi) => (
                        <TableRow key={`${company.company_id}-p${pi}`} sx={{ bgcolor: '#f7f9fc' }}>
                          <TableCell />
                          <TableCell sx={{ py: 0.5, pl: 4 }}>
                            <Typography variant="body2" fontWeight={600}>{person.name}</Typography>
                            {person.title && (
                              <Typography variant="caption" color="text.secondary" display="block">{person.title}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <RoleChip role={person.role} />
                          </TableCell>
                          <TableCell colSpan={2} sx={{ py: 0.5 }}>
                            {person.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                <Link href={`mailto:${person.email}`} sx={{ fontSize: '0.75rem', color: '#1A5490' }}>{person.email}</Link>
                              </Box>
                            )}
                            {(person.business_phone || person.mobile_phone) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                <Typography variant="caption">{person.business_phone || person.mobile_phone}</Typography>
                                {person.business_phone && person.mobile_phone && (
                                  <Typography variant="caption" color="text.secondary"> · M: {person.mobile_phone}</Typography>
                                )}
                              </Box>
                            )}
                            {!person.email && !person.business_phone && !person.mobile_phone && (
                              <Typography variant="caption" color="text.disabled">No contact info</Typography>
                            )}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && companies.length > 200 && (
          <Box sx={{ px: 2, py: 1, bgcolor: '#f5f7fa', borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary">
              Showing first 200 of {companies.length} companies. Use search to narrow results.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// ─── Zoho CRM Full Directory ────────────────────────────────────────────────

const MODULE_COLORS = {
  Contact:  { bg: '#1565C0', label: 'Contact' },
  Account:  { bg: '#2E7D32', label: 'Trade Partner' },
  Lead:     { bg: '#F57C00', label: 'Lead' },
  Project:  { bg: '#5E35B1', label: 'Project' },
  'D-Tools':{ bg: '#00838F', label: 'D-Tools' },
  Manual:   { bg: '#1A5490', label: 'Manual' },
  Archive:  { bg: '#757575', label: 'Archive' },   // v2: former Zoho, frozen archive
};

// Milestone B feature flag — opt clients into V2 unified CRM (crm_contacts backing).
// Set localStorage.crmUnifiedV2 = '1' to enable. Default: v1 for safety until v2 verified.
const CRM_UNIFIED_V2 = (() => {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem('crmUnifiedV2') === '1';
  } catch { return false; }
})();
const UNIFIED_V2_PARAM = CRM_UNIFIED_V2 ? { v2: 1 } : {};

// ----- Manual Contact Dialog (Add/Edit) ----------------------------------
// Full-CRM export button. Authenticates via the shared `api` axios instance
// (Bearer token in localStorage), fetches the XLSX as a blob, and triggers a
// browser download. A raw href would 401 because auth is header-based, not cookie-based.
//
// Opens a small dialog so the user can pick which disciplines to include
// (Architects, Designers, Builders, Clients, Client Reps, trade partners, etc.).
// Empty selection = all contacts.
const EXPORT_PRESETS = [
  { key: 'all',            label: 'All contacts',                     disciplines: [] },
  { key: 'trades_all',     label: 'Trade partners (all disciplines)', disciplines: ['Architect','Builder','Designer','Developer','Electrical Contractor','Engineer','General Contractor','HVAC','Lighting Consultant','Real Estate'] },
  { key: 'clients',        label: 'Clients + Client Reps',            disciplines: ['Client','Client Rep'] },
  { key: 'trades_core',    label: 'Core trades (Elec / MEP / GC / HVAC)', disciplines: ['Electrical Contractor','General Contractor','HVAC','Engineer'] },
  { key: 'aec',            label: 'Architect / Designer / Builder',   disciplines: ['Architect','Designer','Builder'] },
];

const EXPORT_DISCIPLINES = [...DISCIPLINES, 'Untagged'];

function ExportAllContactsButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState([]); // empty = all
  const [hasEmailOnly, setHasEmailOnly] = useState(false);
  const [office, setOffice] = useState('all');

  const toggle = (d) => {
    setSelected(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const applyPreset = (preset) => {
    setSelected(preset.disciplines);
  };

  const doExport = async () => {
    setBusy(true);
    setErr(null);
    try {
      const params = {};
      if (selected.length) params.disciplines = selected.join(',');
      if (hasEmailOnly) params.has_email = 'true';
      if (office && office !== 'all') params.office = office;
      const { data } = await api.get('/crm/export.xlsx', { params, responseType: 'blob' });
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      const slug = selected.length
        ? selected.map(s => s.replace(/[^A-Za-z0-9]+/g, '')).join('-')
        : 'All';
      a.download = `ACS_CRM_${slug}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setOpen(false);
    } catch (e) {
      console.error('CRM export failed', e);
      setErr('Export failed. Try again or contact support.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={() => setOpen(true)}
        sx={{ bgcolor: '#1A5490', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0d3560' } }}
      >
        Export Contacts (XLSX)
      </Button>

      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1A5490' }}>Export CRM Contacts</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary">
            Pick a preset or select disciplines individually. Leave everything unchecked to export the full contact list. Output is XLSX with frozen header + AutoFilter, including Unsubscribed / Hard Bounce flags.
          </Typography>

          <Box sx={{ mt: 2, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>Quick presets</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {EXPORT_PRESETS.map(p => (
                <Chip
                  key={p.key}
                  label={p.label}
                  size="small"
                  onClick={() => applyPreset(p)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>Disciplines</Typography>
          <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            {EXPORT_DISCIPLINES.map(d => (
              <FormControlLabel
                key={d}
                control={<Checkbox size="small" checked={selected.includes(d)} onChange={() => toggle(d)} />}
                label={<Typography variant="body2">{d}</Typography>}
              />
            ))}
          </FormGroup>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={hasEmailOnly} onChange={e => setHasEmailOnly(e.target.checked)} />}
              label={<Typography variant="body2">Email addresses only</Typography>}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Office</InputLabel>
              <Select value={office} label="Office" onChange={e => setOffice(e.target.value)}>
                <MenuItem value="all">All offices</MenuItem>
                <MenuItem value="NY">NY</MenuItem>
                <MenuItem value="FL">FL</MenuItem>
                <MenuItem value="CA">CA</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {selected.length === 0
                ? 'Selection: all contacts'
                : `Selection: ${selected.join(', ')}`}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelected([]); setHasEmailOnly(false); setOffice('all'); }} disabled={busy}>Reset</Button>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={doExport}
            disabled={busy}
            variant="contained"
            startIcon={busy ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
            sx={{ bgcolor: '#1A5490', '&:hover': { bgcolor: '#0d3560' } }}
          >
            {busy ? 'Building\u2026' : 'Download XLSX'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!err}
        autoHideDuration={5000}
        onClose={() => setErr(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>
      </Snackbar>
    </>
  );
}

function ManualContactDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    id: null, first_name: '', last_name: '', title: '', company: '',
    email: '', phone_office: '', phone_mobile: '', website: '',
    mailing_street: '', mailing_city: '', mailing_state: '', mailing_zip: '',
    disciplines: [], notes: '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setForm({
        id: initial?.id || null,
        first_name: initial?.first_name || '',
        last_name: initial?.last_name || '',
        title: initial?.title || initial?.role_title || '',
        company: initial?.company_name || initial?.company || '',
        email: initial?.email || '',
        phone_office: initial?.phone_office || initial?.phone || '',
        phone_mobile: initial?.phone_mobile || initial?.mobile || '',
        website: initial?.website || '',
        mailing_street: initial?.mailing_street || '',
        mailing_city: initial?.mailing_city || '',
        mailing_state: initial?.mailing_state || '',
        mailing_zip: initial?.mailing_zip || '',
        disciplines: Array.isArray(initial?.disciplines) ? initial.disciplines : [],
        notes: initial?.notes || '',
      });
    }
  }, [open, initial]);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const fn = (form.first_name || '').trim();
    const ln = (form.last_name || '').trim();
    if (!fn && !ln) { setError('First name or last name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, name: `${fn} ${ln}`.trim() };
      if (form.id) {
        await updateGlobalContact(form.id, payload);
      } else {
        await createGlobalContact(payload);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{form.id ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField label="First Name"  value={form.first_name}  onChange={update('first_name')}  size="small" autoFocus />
          <TextField label="Last Name"   value={form.last_name}   onChange={update('last_name')}   size="small" />
          <TextField label="Title"       value={form.title}       onChange={update('title')}       size="small" />
          <TextField label="Company"     value={form.company}     onChange={update('company')}     size="small" />
          <TextField label="Email"       value={form.email}       onChange={update('email')}       size="small" type="email" />
          <TextField label="Website"     value={form.website}     onChange={update('website')}     size="small" />
          <TextField label="Office Phone" value={form.phone_office} onChange={update('phone_office')} size="small" />
          <TextField label="Mobile"      value={form.phone_mobile} onChange={update('phone_mobile')} size="small" />
          <TextField label="Street"      value={form.mailing_street} onChange={update('mailing_street')} size="small" sx={{ gridColumn: '1 / span 2' }} />
          <TextField label="City"        value={form.mailing_city}  onChange={update('mailing_city')}  size="small" />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="State" value={form.mailing_state} onChange={update('mailing_state')} size="small" sx={{ flex: 1 }} />
            <TextField label="Zip"   value={form.mailing_zip}   onChange={update('mailing_zip')}   size="small" sx={{ flex: 1 }} />
          </Box>
          <Autocomplete
            multiple
            options={DISCIPLINES}
            value={form.disciplines}
            onChange={(_, v) => setForm(f => ({ ...f, disciplines: v }))}
            renderInput={(params) => <TextField {...params} label="Disciplines" size="small" />}
            sx={{ gridColumn: '1 / span 2' }}
          />
          <TextField label="Notes" value={form.notes} onChange={update('notes')} size="small" multiline rows={2} sx={{ gridColumn: '1 / span 2' }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : null}>
          {form.id ? 'Save Changes' : 'Add Contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ZohoDirectory() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [types, setTypes] = useState([]);
  const [reps, setReps] = useState([]);
  const [repFilter, setRepFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [snack, setSnack] = useState(null);
  const perPage = 100;

  useEffect(() => {
    getZohoContactTypes().then(r => setTypes(r.data.types || [])).catch(() => {});
    getZohoReps().then(r => setReps(r.data.reps || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, per_page: perPage, ...UNIFIED_V2_PARAM };
      if (moduleFilter !== 'all') params.source = moduleFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (repFilter !== 'all') params.rep = repFilter;
      if (search) params.search = search;
      const res = await getUnifiedContacts(params);
      setRecords(res.data.records || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [search, moduleFilter, typeFilter, repFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, moduleFilter, typeFilter, repFilter]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.ceil(total / perPage);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <PageSearchBar
            placeholder="Search name, company, email, phone…"
            value={searchInput}
            onChange={(v) => setSearchInput(v)}
          />

          <ToggleButtonGroup
            value={moduleFilter}
            exclusive
            onChange={(_, v) => { if (v) setModuleFilter(v); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.8rem', textTransform: 'none' } }}
          >
            <ToggleButton value="all">All ({stats.total || 0})</ToggleButton>
            <ToggleButton value="Contact">Contacts ({stats.contacts || 0})</ToggleButton>
            <ToggleButton value="Account">Trade Partners ({stats.accounts || 0})</ToggleButton>
            <ToggleButton value="Lead">Leads ({stats.leads || 0})</ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
              <MenuItem value="all">All Types</MenuItem>
              {types.map(t => (
                <MenuItem key={t.contact_type} value={t.contact_type}>{t.contact_type} ({t.cnt})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>ACS Rep</InputLabel>
            <Select value={repFilter} label="ACS Rep" onChange={e => setRepFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
              <MenuItem value="all">All Reps</MenuItem>
              {reps.map(r => (
                <MenuItem key={r.owner_name} value={r.owner_name}>{r.owner_name} ({r.cnt})</MenuItem>
              ))}
            </Select>
          </FormControl>

          {!loading && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {total} unique contacts · {stats.duplicates_removed || 0} duplicates removed · {stats.with_email || 0} with email
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => { setEditing(null); setDialogOpen(true); }}
                sx={{ textTransform: 'none', bgcolor: '#1A5490', '&:hover': { bgcolor: '#0e3a66' } }}
              >
                Add Contact
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <ManualContactDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={() => { load(); setSnack({ msg: editing ? 'Contact updated' : 'Contact added', severity: 'success' }); }}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        message={snack?.msg || ''}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Contact Info</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>ACS Rep</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem', width: 80 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={28} /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading…</Typography></TableCell></TableRow>
              ) : records.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><Typography variant="body2" color="text.secondary">No records found.</Typography></TableCell></TableRow>
              ) : (
                records.map((r, idx) => {
                  const modCfg = MODULE_COLORS[r.source_detail || r.zoho_module] || { bg: '#757575', label: r.source_detail || r.zoho_module || '—' };
                  return (
                    <TableRow key={r.id} hover sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="body2" fontWeight={700}>{r.name || r.full_name || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Chip label={modCfg.label} size="small" sx={{ bgcolor: modCfg.bg, color: '#fff', fontWeight: 600, fontSize: '0.68rem', height: 20 }} />
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        {(r.type_raw || r.contact_type) ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(r.type_raw || r.contact_type || '').replace(/[{}"]/g, '').split(',').filter(Boolean).map(t => { t = t.trim();
                              const cfg = ROLE_COLORS[t.toLowerCase()] || ROLE_COLORS[t.toLowerCase().replace(/ /g, '_')] || { bg: '#78909C', label: t };
                              return <Chip key={t} label={cfg.label || t} size="small" sx={{ bgcolor: cfg.bg, color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 18 }} />;
                            })}
                          </Box>
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{r.company_name || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, minWidth: 180 }}>
                        {r.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                            <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Link href={`mailto:${r.email}`} sx={{ fontSize: '0.73rem', color: '#1A5490', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{r.email}</Link>
                          </Box>
                        )}
                        {(r.phone || r.mobile) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Typography variant="caption">{r.phone || r.mobile}</Typography>
                          </Box>
                        )}
                        {!r.email && !r.phone && !r.mobile && <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        {r.acs_rep ? (
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{r.acs_rep}</Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>{r.title || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }} align="center">
                        {r.source === 'manual' ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => { setEditing(r); setDialogOpen(true); }}><EditIcon fontSize="inherit" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={async () => {
                                if (!window.confirm(`Delete ${r.name}? This cannot be undone.`)) return;
                                try {
                                  await deleteGlobalContact(r.manual_id);
                                  setSnack({ msg: 'Contact deleted', severity: 'success' });
                                  load();
                                } catch (err) {
                                  setSnack({ msg: err.response?.data?.error || 'Delete failed', severity: 'error' });
                                }
                              }}><DeleteIcon fontSize="inherit" /></IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="Zoho contacts can't be edited here — update in Zoho">
                            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>—</Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: '#f5f7fa', borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary">{total} total records</Typography>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" />
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// ─── Main CRM Page ────────────────────────────────────────────────────────────

function MailingListBuilder() {
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importCompany, setImportCompany] = useState('');
  const [importText, setImportText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [hasEmail, setHasEmail] = useState(true);
  const [hasPhone, setHasPhone] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);
  const [office, setOffice] = useState('all');
  const [count, setCount] = useState(0);
  const [emailCount, setEmailCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);

  const loadCompanies = useCallback(async () => {
    try {
      const r = await getCompaniesForMailing();
      setCompanyOptions(r.data.companies || []);
    } catch (err) { /* non-fatal */ }
  }, []);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const load = useCallback(async () => {
    if (selectedDisciplines.length === 0 && selectedCompanies.length === 0) {
      setCount(0); setEmailCount(0); setRecords([]); setEmails([]);
      return;
    }
    setLoading(true);
    try {
      const params = {
        has_email: hasEmail ? 'true' : 'false',
        has_phone: hasPhone ? 'true' : 'false',
        has_address: hasAddress ? 'true' : 'false',
        office,
        ...UNIFIED_V2_PARAM,
      };
      if (selectedDisciplines.length) params.disciplines = selectedDisciplines.join(',');
      if (selectedCompanies.length) params.companies = selectedCompanies.join(',');
      const res = await getMailingList(params);
      setCount(res.data.count || 0);
      setEmailCount(res.data.unique_email_count || 0);
      setRecords(res.data.records || []);
      setEmails(res.data.emails || []);
    } catch (err) {
      setSnack({ msg: err.response?.data?.error || 'Failed to build list' });
    } finally {
      setLoading(false);
    }
  }, [selectedDisciplines, selectedCompanies, hasEmail, hasPhone, hasAddress, office]);

  useEffect(() => { load(); }, [load]);

  const toggleDiscipline = (d) => {
    setSelectedDisciplines(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };
  const selectAll = () => setSelectedDisciplines([...DISCIPLINES]);
  const clearAll  = () => setSelectedDisciplines([]);

  const copyEmails = async () => {
    if (!emails.length) return;
    try {
      await navigator.clipboard.writeText(emails.join(', '));
      setSnack({ msg: `Copied ${emails.length} emails to clipboard` });
    } catch {
      const ta = document.createElement('textarea');
      ta.value = emails.join(', ');
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setSnack({ msg: `Copied ${emails.length} emails to clipboard` });
    }
  };

  const csvEscape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };

  const exportCSV = () => {
    if (!records.length) return;
    const header = ['Email Address','First Name','Last Name','Company','Address','City','State','Zip','Phone','Discipline'];
    const lines = [header.join(',')];
    for (const r of records) {
      lines.push([
        csvEscape(r.email),
        csvEscape(r.first_name),
        csvEscape(r.last_name),
        csvEscape(r.company_name),
        csvEscape(r.mailing_street),
        csvEscape(r.mailing_city),
        csvEscape(r.mailing_state),
        csvEscape(r.mailing_zip),
        csvEscape(r.phone || r.mobile),
        csvEscape(Array.isArray(r.disciplines) ? r.disciplines.join('; ') : ''),
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0,10);
    a.download = `acs-mailing-list-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSnack({ msg: `Exported ${records.length} rows to CSV` });
  };

  const parseAttendees = (text) => {
    const emailRe = /[\w.+-]+@[\w-]+\.[\w.-]+/;
    const out = [];
    for (const line of String(text || '').split(/\r?\n/)) {
      const s = line.trim(); if (!s) continue;
      const m = s.match(emailRe); if (!m) continue;
      const email = m[0];
      let name = s.replace(email, '').replace(/mailto\s*:?/gi, '').replace(/[\u2014\u2013\-<>()\[\]:,;|]+/g, ' ').replace(/\s+/g, ' ').trim();
      const parts = name.split(' ');
      let first = null, last = null;
      if (parts.length >= 2) { first = parts.slice(0, -1).join(' '); last = parts.slice(-1)[0]; }
      else if (parts.length === 1 && parts[0]) { first = parts[0]; }
      out.push({ first_name: first, last_name: last, email });
    }
    return out;
  };

  const parsedImport = React.useMemo(() => parseAttendees(importText), [importText]);

  const doImport = async () => {
    const name = importCompany.trim();
    if (!name) { setSnack({ msg: 'Enter a company name.' }); return; }
    if (!parsedImport.length) { setSnack({ msg: 'No valid attendee lines found.' }); return; }
    setImportBusy(true);
    try {
      const r = await bulkImportContacts({ company_name: name, contacts: parsedImport });
      const d = r.data || {};
      setSnack({ msg: `Imported to ${name}: ${d.created || 0} created, ${d.updated || 0} updated${(d.skipped || []).length ? ', ' + d.skipped.length + ' skipped' : ''}.` });
      setImportOpen(false);
      setImportText('');
      await loadCompanies();
      // Auto-select the imported company so the list rebuilds with it selected.
      setSelectedCompanies(prev => prev.includes(name) ? prev : [...prev, name]);
      setImportCompany('');
    } catch (err) {
      setSnack({ msg: err.response?.data?.error || 'Import failed' });
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>1. Pick companies (optional)</Typography>
          <Button size="small" variant="outlined" onClick={() => setImportOpen(true)} sx={{ textTransform: 'none' }}>
            + Import L&amp;L attendees
          </Button>
        </Box>
        <Autocomplete
          multiple
          size="small"
          options={companyOptions}
          value={companyOptions.filter(o => selectedCompanies.includes(o.company_name))}
          onChange={(_, vals) => setSelectedCompanies(vals.map(v => (typeof v === 'string' ? v : v.company_name)))}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.company_name || '')}
          isOptionEqualToValue={(a, b) => (a?.company_name || a) === (b?.company_name || b)}
          renderOption={(props, option) => (
            <li {...props} key={option.company_name}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{option.company_name}</span>
                <Typography variant="caption" color="text.secondary">{option.email_count || 0} emails · {option.contact_count || 0} contacts</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} placeholder="Filter by company — e.g. Davies & Toews" />}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>2. Pick disciplines (optional)</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button size="small" onClick={selectAll} sx={{ textTransform: 'none' }}>Select all</Button>
          <Button size="small" onClick={clearAll} sx={{ textTransform: 'none' }}>Clear</Button>
        </Box>
        <FormGroup row sx={{ mb: 2 }}>
          {DISCIPLINES.map(d => (
            <FormControlLabel
              key={d}
              control={<Checkbox size="small" checked={selectedDisciplines.includes(d)} onChange={() => toggleDiscipline(d)} />}
              label={<Typography variant="body2">{d}</Typography>}
              sx={{ minWidth: 200 }}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>2. Filters</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <FormControlLabel control={<Switch checked={hasEmail} onChange={e => setHasEmail(e.target.checked)} />} label="Has email" />
          <FormControlLabel control={<Switch checked={hasPhone} onChange={e => setHasPhone(e.target.checked)} />} label="Has phone" />
          <FormControlLabel control={<Switch checked={hasAddress} onChange={e => setHasAddress(e.target.checked)} />} label="Has address" />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Office</InputLabel>
            <Select value={office} label="Office" onChange={e => setOffice(e.target.value)}>
              <MenuItem value="all">All offices</MenuItem>
              {ACS_OFFICES.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700} color="#1A5490">
            {loading ? <CircularProgress size={20} sx={{ mr: 1, verticalAlign: 'middle' }} /> : null}
            {count.toLocaleString()} {count === 1 ? 'person' : 'people'} match
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({emailCount.toLocaleString()} unique email{emailCount === 1 ? '' : 's'})
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            disabled={!emails.length || loading}
            onClick={copyEmails}
            sx={{ textTransform: 'none' }}
          >
            Copy Emails
          </Button>
          <Button
            variant="contained"
            disabled={!records.length || loading}
            onClick={exportCSV}
            sx={{ textTransform: 'none', bgcolor: '#1A5490', '&:hover': { bgcolor: '#0e3a66' } }}
          >
            Export Mailchimp CSV
          </Button>
        </Box>

        {selectedDisciplines.length === 0 && selectedCompanies.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Pick one or more companies OR disciplines to build a list. Client Rep is a separate audience &mdash; check it explicitly if you want to include them.
          </Alert>
        )}
      </Paper>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Import Lunch &amp; Learn attendees</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Paste attendee lines from the L&amp;L thread — we’ll upsert them into the CRM under the company you choose. Then export the mailing list.
          </Typography>
          <Autocomplete
            freeSolo
            size="small"
            options={companyOptions.map(o => o.company_name)}
            value={importCompany}
            onChange={(_, v) => setImportCompany(v || '')}
            onInputChange={(_, v) => setImportCompany(v || '')}
            renderInput={(params) => <TextField {...params} label="Company" placeholder="e.g. Davies & Toews" required />}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Paste attendees — one per line"
            placeholder={'Kathleen Bailey — katie@daviestoews.com\nAdam Strogel — adam@daviestoews.com'}
            multiline
            minRows={6}
            fullWidth
            value={importText}
            onChange={e => setImportText(e.target.value)}
            helperText="Any line containing an email works: 'Name — email', 'Name <email>', etc."
          />
          {parsedImport.length > 0 && (
            <Box sx={{ mt: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="caption" sx={{ px: 1, py: 0.5, display: 'block', bgcolor: '#f5f7fa' }}>Preview — {parsedImport.length} parsed</Typography>
              <Table size="small">
                <TableBody>
                  {parsedImport.slice(0, 20).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{r.email}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={importBusy || !parsedImport.length || !importCompany.trim()} onClick={doImport} sx={{ bgcolor: '#1A5490' }}>
            {importBusy ? 'Importing…' : `Import ${parsedImport.length || ''} to CRM`}
          </Button>
        </DialogActions>
      </Dialog>

      {records.length > 0 && (
        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary">Preview — first 200 rows shown</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Disciplines</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>Phone</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.slice(0, 200).map((r, i) => (
                  <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{r.email}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{r.company_name || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {Array.isArray(r.disciplines) && r.disciplines.length
                        ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {r.disciplines.map(d => <Chip key={d} label={d} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />)}
                          </Box>
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{r.phone || r.mobile || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} message={snack?.msg || ''} />
    </Box>
  );
}

export default function CRM() {
  const [activeTab, setActiveTab] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [repFilter, setRepFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Sort
  const [sortField, setSortField] = useState('project_count');
  const [sortDir, setSortDir] = useState('desc');

  // Load reps once
  useEffect(() => {
    getCRMReps()
      .then(r => setReps(r.data.reps || []))
      .catch(() => {});
  }, []);

  // Load contacts on filter/sort change
  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        type: typeFilter,
        sort: sortField,
        dir: sortDir,
      };
      if (roleFilter) params.role = roleFilter;
      if (repFilter)  params.rep  = repFilter;
      if (search)     params.search = search;

      const res = await getCRMContacts(params);
      setContacts(res.data.contacts || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, roleFilter, repFilter, search, sortField, sortDir]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const totalValue = contacts.reduce((sum, c) => sum + (c.total_contract_value || 0), 0);

  return (
    <Box sx={{ p: 0 }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <ContactsIcon sx={{ fontSize: 32, color: '#1A5490' }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1A5490">
            Command Center CRM
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Unified directory — all contacts grouped by company, deduplicated across Zoho CRM, D-Tools, and project data
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Full-CRM export — streams XLSX from the server so this scales past the
            browser export limits. Frozen header + AutoFilter applied server-side.
            Must go through `api` (Bearer token from localStorage), not a raw href. */}
        <ExportAllContactsButton />
      </Box>

      <Paper elevation={0} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0', borderRadius: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 42 } }}
        >
          <Tab label="Companies" />
          <Tab label="All Contacts" />
          <Tab label="Mailing List Builder" />
        </Tabs>
      </Paper>

      {activeTab === 0 && <CompanyDirectory />}
      {activeTab === 1 && <ZohoDirectory />}
      {activeTab === 2 && <MailingListBuilder />}

      {false && (
      <>
      {/* ── Legacy Filter Bar (fallback) ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

          {/* Type toggle */}
          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            onChange={(_, v) => { if (v) { setTypeFilter(v); setRoleFilter(''); } }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.8rem', textTransform: 'none' } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="trade">Trade Partners</ToggleButton>
            <ToggleButton value="client">Clients</ToggleButton>
          </ToggleButtonGroup>

          {/* Role filter (only when trade selected) */}
          {typeFilter !== 'client' && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ fontSize: '0.8rem' }}>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={e => setRoleFilter(e.target.value)}
                sx={{ fontSize: '0.8rem' }}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="architect">Architect</MenuItem>
                <MenuItem value="builder">Builder</MenuItem>
                <MenuItem value="general_contractor">General Contractor</MenuItem>
                <MenuItem value="interior_designer">Designer</MenuItem>
                <MenuItem value="lighting_consultant">Lighting Consultant</MenuItem>
                <MenuItem value="client_rep">Client Rep</MenuItem>
                <MenuItem value="electrician">Electrician</MenuItem>
                <MenuItem value="developer">Developer</MenuItem>
                <MenuItem value="real_estate">Real Estate</MenuItem>
                <MenuItem value="engineer">Engineer</MenuItem>
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="construction">Construction</MenuItem>
                <MenuItem value="hvac">HVAC</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Sales rep filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Sales Rep</InputLabel>
            <Select
              value={repFilter}
              label="Sales Rep"
              onChange={e => setRepFilter(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="">All Reps</MenuItem>
              {reps.map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Search */}
          <PageSearchBar
            placeholder="Search company, contact, job #…"
            value={searchInput}
            onChange={(v) => setSearchInput(v)}
          />
        </Box>
      </Paper>

      {/* ── Main Table ─────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel
                    active={sortField === 'company_name'}
                    direction={sortField === 'company_name' ? sortDir : 'asc'}
                    onClick={() => handleSort('company_name')}
                  >
                    Company / Contact
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  Type
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  Contact Info
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  Sales Rep
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel
                    active={sortField === 'project_count'}
                    direction={sortField === 'project_count' ? sortDir : 'desc'}
                    onClick={() => handleSort('project_count')}
                  >
                    Projects
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.78rem' }}>
                  <TableSortLabel
                    active={sortField === 'total_contract_value'}
                    direction={sortField === 'total_contract_value' ? sortDir : 'desc'}
                    onClick={() => handleSort('total_contract_value')}
                  >
                    Total Value
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ bgcolor: '#f5f7fa', width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading contacts…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No contacts found matching current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact, i) => (
                  <ContactRow key={`${contact.type}-${contact.company_name}-${i}`} contact={contact} index={i} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && contacts.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f7fa' }}>
              <Typography variant="caption" color="text.secondary">
                {contacts.length} contact{contacts.length !== 1 ? 's' : ''} · click any row to expand projects
              </Typography>
              <Typography variant="caption" fontWeight={600} color="#1A5490">
                Total active contract value: {fmtCurrency(totalValue)}
              </Typography>
            </Box>
          </>
        )}
      </Paper>
      </>
      )}
    </Box>
  );
}
